import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useToastStore, type ToastMessage } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import { STATUS_SHELL, STATUS_TONE } from './statusTone';

/**
 * Where every toast in the product is drawn. Mounted once, at the app root.
 *
 * Bottom-right on a desktop and full width along the bottom on a phone, out of
 * the way of the page's own actions. The stack itself ignores pointer events so
 * it can never swallow a click meant for the page underneath.
 *
 * One deliberate limit: a native `<dialog>` opened with `showModal()` lives in
 * the browser's top layer, above anything CSS can reach. A toast raised while a
 * dialog is open would be hidden behind it, so dialogs keep their own inline
 * error and toasts report what happened once the dialog has closed.
 */

const SUPPORTS_POPOVER =
  typeof HTMLElement !== 'undefined' && 'popover' in HTMLElement.prototype;

export function Toaster() {
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || !SUPPORTS_POPOVER) return;

    if (toasts.length > 0) {
      if (!node.hasAttribute('popover')) {
        node.setAttribute('popover', 'manual');
      }
      try {
        node.hidePopover();
      } catch {}
      try {
        node.showPopover();
      } catch {}
    } else {
      if (node.hasAttribute('popover')) {
        try {
          node.hidePopover();
        } catch {}
      }
    }
  }, [toasts]);

  if (toasts.length === 0) return null;

  return (
    <div
      ref={containerRef}
      // `polite` so a confirmation waits for the screen reader to finish what
      // it was saying; a critical toast carries role="alert" and interrupts.
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      className={cn(
        'pointer-events-none fixed z-[9999] flex flex-col gap-2',
        'bottom-3 right-3 left-3 sm:left-auto sm:bottom-4 sm:right-4 w-auto sm:w-[22rem]',
        'm-0 p-0 border-0 bg-transparent shadow-none overflow-visible',
      )}
    >
      {toasts.map((toast) => (
        <ToastRow key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
      ))}
    </div>
  );
}

function ToastRow({ toast, onDismiss }: { toast: ToastMessage; onDismiss: () => void }) {
  // The same tone table the inline `Alert` reads, so a message says the same
  // thing whether it lands in the corner or on the page (CLAUDE.md §3).
  const { icon, tile, bar } = STATUS_TONE[toast.tone];

  // Hovering or focusing holds the message: reading a two-line failure takes
  // longer than the timer allows, and it must not vanish mid-sentence.
  const [isHeld, setIsHeld] = useState(false);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (toast.duration === 0 || isHeld) return;
    const timer = setTimeout(() => onDismissRef.current(), toast.duration);
    return () => clearTimeout(timer);
  }, [toast.duration, isHeld]);

  return (
    <div
      role={toast.tone === 'critical' ? 'alert' : 'status'}
      onMouseEnter={() => setIsHeld(true)}
      onMouseLeave={() => setIsHeld(false)}
      onFocusCapture={() => setIsHeld(true)}
      onBlurCapture={() => setIsHeld(false)}
      className={cn(STATUS_SHELL, 'pointer-events-auto animate-fade-up')}
    >
      <span className={cn('absolute inset-y-0 left-0 w-1', bar)} aria-hidden />

      <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-xl', tile)} aria-hidden>
        {icon}
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-ink">{toast.title}</p>
        {toast.description && <p className="mt-0.5 text-sm text-ink-2">{toast.description}</p>}
      </div>

      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="-mr-1 -mt-0.5 shrink-0 rounded-lg p-1.5 text-ink-3 transition-colors hover:bg-surface-2 hover:text-ink"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
